/**
 * NOT-584 — the fields a settings PATCH should actually carry.
 *
 * A settings tab seeds its draft from the GET response and, left alone, PATCHes
 * the whole draft back on save. That turns "I changed the expiry to 45 days"
 * into a write of all thirteen fields, and the read side resolves absent values
 * to their CURRENT defaults before handing them over — so saving one unrelated
 * field silently persists every default the tenant happened to be shown.
 *
 * That is not cosmetic. `estimateNameTemplate` is the case that proved it: the
 * pre-NOT-584 default was written onto every tenant who opened this tab and
 * saved anything, and once stored it is byte-identical to a template the tenant
 * genuinely chose. `tenant.settings` is a single Json column with no per-key
 * provenance, so the two are indistinguishable afterwards — no migration can
 * separate them, which is why the rename cannot be rolled out to that
 * population without risking an overwrite of a real choice.
 *
 * Sending only what changed is what stops the next default from doing the same
 * thing. It does not repair the rows already written; nothing safely can.
 *
 * PATCH semantics are on our side here: an omitted key means "leave it alone",
 * which is exactly true of a field the user never touched.
 */
export function pickChangedFields<T extends object>(
  draft: T,
  baseline: T | null | undefined,
): Partial<T> {
  // No baseline means the form never hydrated, so nothing is known to be
  // unchanged. Sending everything is the old behaviour and the safe floor —
  // the alternative is dropping edits the user really did make.
  if (!baseline) return { ...draft };

  const changed: Partial<T> = {};
  for (const key of Object.keys(draft) as (keyof T)[]) {
    // Object.is, not ===, so a NaN expiry compares equal to itself rather than
    // being re-sent on every save. Every field on these drafts is a primitive;
    // a nested object would compare by identity and always look changed, which
    // is why the callers destructure those out.
    if (!Object.is(draft[key], baseline[key])) {
      changed[key] = draft[key];
    }
  }
  return changed;
}
